import React, { useState, useEffect } from "react";
import apiService from "../services/api";

// Helper to build tree from flat list
function buildTree(categories) {
  const map = {};
  const roots = [];
  categories.forEach((cat) => (map[cat.id] = { ...cat, children: [] }));
  categories.forEach((cat) => {
    if (cat.parentId) {
      map[cat.parentId]?.children.push(map[cat.id]);
    } else {
      roots.push(map[cat.id]);
    }
  });
  return roots;
}

// Hierarchical Category Selector Component
const CategorySelector = ({
  categories,
  selectedCategoryId,
  onCategoryChange,
  loading,
  label = "Category",
}) => {
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const tree = buildTree(categories);

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getCategoryPath = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return "";

    const path = [category.name];
    let current = category;

    while (current.parentId) {
      current = categories.find((cat) => cat.id === current.parentId);
      if (current) {
        path.unshift(current.name);
      } else {
        break;
      }
    }

    return path.join(" > ");
  };

  const renderCategoryOption = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded[node.id];
    const isSelected = selectedCategoryId === node.id;

    return (
      <div key={node.id}>
        <div
          className={`flex items-center px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors duration-200 ${
            isSelected ? "bg-blue-100 text-blue-700" : ""
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            onCategoryChange(node.id);
            setShowDropdown(false);
          }}
        >
          {hasChildren && (
            <button
              className="w-4 h-4 mr-2 flex items-center justify-center text-gray-500 hover:text-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle(node.id);
              }}
            >
              <svg
                className={`w-3 h-3 transition-transform duration-200 ${
                  isExpanded ? "rotate-90" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
          {!hasChildren && <div className="w-4 h-4 mr-2"></div>}
          <div className="flex items-center gap-2">
            <span className="text-sm">{node.name}</span>
            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-mono">
              {node.code}
            </span>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child) =>
              renderCategoryOption(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredTree = searchTerm
    ? tree.filter(
        (node) =>
          node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          node.children.some((child) =>
            child.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
      )
    : tree;

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white h-[42px]"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={loading}
        >
          {loading ? (
            <span className="text-gray-500">Loading categories...</span>
          ) : selectedCategoryId ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-900">
                {getCategoryPath(selectedCategoryId)}
              </span>
              {(() => {
                const category = categories.find(
                  (cat) => cat.id === selectedCategoryId
                );
                return category ? (
                  <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-mono">
                    {category.code}
                  </span>
                ) : null;
              })()}
            </div>
          ) : (
            <span className="text-gray-500">Select Category</span>
          )}
          <svg
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {showDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search categories..."
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
            <div className="py-1">
              {filteredTree.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No categories found
                </div>
              ) : (
                filteredTree.map((node) => renderCategoryOption(node))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductForm = ({ open, onClose, onSave, initialData = {} }) => {
  const [form, setForm] = useState({
    // Basic Information (Product Level)
    name: "",
    itemCode: "",
    description: "",
    detailedDescription: "",
    category: "",
    subCategory: "",
    primaryCategoryId: null, // New field for multilevel categorization
    brand: "",
    model: "",

    // Pricing (Product Level)
    gstPercent: "",
    discountEligibility: false,

    // SKUs Array
    skus: [],

    // Specifications
    itemType: "",
    productLine: "",
    colorCode: "",
    baseType: "",
    finishType: "",
    unitOfMeasure: "",
    packSize: "",
    material: "",
    grade: "",

    // Industrial Features
    hsCode: "",
    isHazmat: false,
    countryOfOrigin: "",
    batchControlled: false,
    shelfLifeMonths: "",
    storageConditions: "",
    specialInstructions: "",

    // Inventory Management
    minimumOrderQuantity: "1",
    maximumOrderQuantity: "",
    leadTime: "",
    reorderPoint: "",
    isActive: true,

    // Variants
    variants: [],

    // Packaging Levels
    packagingLevels: [],

    // Images
    imageUrls: [],

    // Advanced Features
    specifications: {},
    industryFeatures: {},
    packagingOptions: {},
    visibilityRoles: [],
  });

  const [activeTab, setActiveTab] = useState("basic");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Add Category Modal State
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    code: "",
    parentId: null,
  });
  const [addingCategory, setAddingCategory] = useState(false);

  // Predefined options
  const productTypes = [
    "Raw Material",
    "Finished Good",
    "Semi-Finished",
    "Component",
    "Assembly",
    "Tool",
    "Equipment",
    "Consumable",
  ];

  const unitsOfMeasure = [
    "Pieces",
    "Kilograms",
    "Meters",
    "Liters",
    "Square Meters",
    "Cubic Meters",
    "Boxes",
    "Cartons",
    "Pallets",
  ];

  const materials = [
    "Steel",
    "Aluminum",
    "Copper",
    "Plastic",
    "Wood",
    "Glass",
    "Ceramic",
    "Composite",
    "Other",
  ];

  const grades = [
    "Commercial",
    "Industrial",
    "Premium",
    "Standard",
    "High Grade",
    "Low Grade",
    "Food Grade",
    "Medical Grade",
  ];

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await apiService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Handle adding a new category
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      return;
    }

    try {
      setAddingCategory(true);

      // Auto-generate code if not provided
      let categoryCode = newCategory.code.trim();
      if (!categoryCode) {
        const nextCodeResponse = await apiService.getNextCategoryCode();
        categoryCode = nextCodeResponse.nextCode;
      }

      const categoryData = {
        name: newCategory.name.trim(),
        code: categoryCode,
        parentId: newCategory.parentId || null,
      };

      const createdCategory = await apiService.createCategory(categoryData);

      // Refresh categories list
      await fetchCategories();

      // Auto-select the newly created category
      setForm((prev) => ({
        ...prev,
        primaryCategoryId: createdCategory.id,
        category: createdCategory.name,
      }));

      // Reset and close modal
      setNewCategory({ name: "", code: "", parentId: null });
      setShowAddCategoryModal(false);
    } catch (error) {
      console.error("Error creating category:", error);
      setError(error.message || "Failed to create category");
    } finally {
      setAddingCategory(false);
    }
  };

  // Handle category selection
  const handleCategoryChange = (categoryId) => {
    const category = categories.find((cat) => cat.id === parseInt(categoryId));
    setForm((prev) => ({
      ...prev,
      primaryCategoryId: categoryId ? parseInt(categoryId) : null,
      category: category ? category.name : "",
      subCategory: "", // Reset subcategory when category changes
    }));
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  useEffect(() => {
    if (initialData && open) {
      setForm({
        name: initialData.name || "",
        itemCode: initialData.itemCode || "",
        sku: initialData.sku || "",
        barcode: initialData.barcode || "",
        description: initialData.description || "",
        detailedDescription: initialData.detailedDescription || "",
        category: initialData.category || "",
        subCategory: initialData.subCategory || "",
        primaryCategoryId: initialData.primaryCategoryId || null,
        brand: initialData.brand || "",
        model: initialData.model || "",
        gstPercent: initialData.gstPercent || "",
        discountEligibility: initialData.discountEligibility || false,
        skus: initialData.skus || [],
        itemType: initialData.itemType || "",
        productLine: initialData.productLine || "",
        colorCode: initialData.colorCode || "",
        baseType: initialData.baseType || "",
        finishType: initialData.finishType || "",
        unitOfMeasure: initialData.unitOfMeasure || "",
        packSize: initialData.packSize || "",
        weight: initialData.weight || "",
        dimensions: initialData.dimensions || "",
        material: initialData.material || "",
        grade: initialData.grade || "",
        hsCode: initialData.hsCode || "",
        isHazmat: initialData.isHazmat || false,
        countryOfOrigin: initialData.countryOfOrigin || "",
        batchControlled: initialData.batchControlled || false,
        shelfLifeMonths: initialData.shelfLifeMonths || "",
        storageConditions: initialData.storageConditions || "",
        specialInstructions: initialData.specialInstructions || "",
        minimumOrderQuantity: initialData.minimumOrderQuantity || "1",
        maximumOrderQuantity: initialData.maximumOrderQuantity || "",
        leadTime: initialData.leadTime || "",
        reorderPoint: initialData.reorderPoint || "",
        isActive:
          initialData.isActive !== undefined ? initialData.isActive : true,
        variants: initialData.variants || [],
        packagingLevels: initialData.packagingLevels || [],
        imageUrls: initialData.imageUrls || [],
        specifications: initialData.specifications || {},
        industryFeatures: initialData.industryFeatures || {},
        packagingOptions: initialData.packagingOptions || {},
        visibilityRoles: initialData.visibilityRoles || [],
      });
    } else if (open) {
      setForm({
        name: "",
        itemCode: "",
        description: "",
        detailedDescription: "",
        category: "",
        subCategory: "",
        primaryCategoryId: null,
        brand: "",
        model: "",
        gstPercent: "",
        discountEligibility: false,
        skus: [],
        itemType: "",
        productLine: "",
        colorCode: "",
        baseType: "",
        finishType: "",
        unitOfMeasure: "",
        packSize: "",
        material: "",
        grade: "",
        hsCode: "",
        isHazmat: false,
        countryOfOrigin: "",
        batchControlled: false,
        shelfLifeMonths: "",
        storageConditions: "",
        specialInstructions: "",
        minimumOrderQuantity: "1",
        maximumOrderQuantity: "",
        leadTime: "",
        reorderPoint: "",
        isActive: true,
        variants: [],
        packagingLevels: [],
        imageUrls: [],
        specifications: {},
        industryFeatures: {},
        packagingOptions: {},
        visibilityRoles: [],
      });
    }
  }, [initialData, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          id: Date.now(),
          name: "",
          sku: "",
          price: "",
          bulkPricePerLiter: "",
          weight: "",
          isActive: true,
        },
      ],
    }));
  };

  const removeVariant = (index) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const updateVariant = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, i) =>
        i === index ? { ...variant, [field]: value } : variant
      ),
    }));
  };

  // SKU Management Functions
  const addSku = () => {
    setForm((prev) => ({
      ...prev,
      skus: [
        ...prev.skus,
        {
          id: Date.now(),
          sku: "",
          barcode: "",
          name: "",
          description: "",
          costPrice: "",
          sellingPrice: "",
          weight: "",
          dimensions: "",
          isActive: true,
        },
      ],
    }));
  };

  const removeSku = (index) => {
    setForm((prev) => ({
      ...prev,
      skus: prev.skus.filter((_, i) => i !== index),
    }));
  };

  const updateSku = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      skus: prev.skus.map((sku, i) =>
        i === index ? { ...sku, [field]: value } : sku
      ),
    }));
  };

  const addPackagingLevel = () => {
    setForm((prev) => ({
      ...prev,
      packagingLevels: [
        ...prev.packagingLevels,
        {
          id: Date.now(),
          level: "",
          description: "",
          quantity: "",
          weight: "",
          dimensions: "",
          material: "",
          cost: "",
        },
      ],
    }));
  };

  const removePackagingLevel = (index) => {
    setForm((prev) => ({
      ...prev,
      packagingLevels: prev.packagingLevels.filter((_, i) => i !== index),
    }));
  };

  const updatePackagingLevel = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      packagingLevels: prev.packagingLevels.map((level, i) =>
        i === index ? { ...level, [field]: value } : level
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      // Validation
      if (
        !form.name ||
        !form.itemCode ||
        !form.costPrice ||
        !form.sellingPrice
      ) {
        throw new Error(
          "Name, Item Code, Cost Price, and Selling Price are required"
        );
      }

      if (parseFloat(form.costPrice) < 0 || parseFloat(form.sellingPrice) < 0) {
        throw new Error("Prices must be positive numbers");
      }

      // Prepare data
      const saveData = {
        ...form,
        costPrice: parseFloat(form.costPrice),
        sellingPrice: parseFloat(form.sellingPrice),
        gstPercent: form.gstPercent ? parseFloat(form.gstPercent) : null,
        weight: form.weight ? parseFloat(form.weight) : null,
        shelfLifeMonths: form.shelfLifeMonths
          ? parseInt(form.shelfLifeMonths)
          : null,
        minimumOrderQuantity: parseInt(form.minimumOrderQuantity),
        maximumOrderQuantity: form.maximumOrderQuantity
          ? parseInt(form.maximumOrderQuantity)
          : null,
        leadTime: form.leadTime ? parseInt(form.leadTime) : null,
        reorderPoint: form.reorderPoint ? parseInt(form.reorderPoint) : null,
      };

      await onSave(saveData);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // Image upload handlers
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      // For now, we'll use base64 encoding for images
      // In production, you'd upload to a CDN/cloud storage
      const imagePromises = files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const base64Images = await Promise.all(imagePromises);
      setForm((prev) => ({
        ...prev,
        imageUrls: [...(prev.imageUrls || []), ...base64Images],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index),
    }));
  };

  const handleSetPrimaryImage = (index) => {
    setForm((prev) => {
      const newImages = [...prev.imageUrls];
      const [primary] = newImages.splice(index, 1);
      return {
        ...prev,
        imageUrls: [primary, ...newImages],
      };
    });
  };

  const tabs = [
    { id: "basic", label: "Basic Info", icon: "📦" },
    { id: "media", label: "Media", icon: "🖼️" },
    { id: "skus", label: "SKUs & Barcodes", icon: "🏷️" },
    { id: "specifications", label: "Specifications", icon: "⚙️" },
    { id: "variants", label: "Variants", icon: "🔄" },
    { id: "packaging", label: "Packaging", icon: "📦" },
    { id: "industrial", label: "Industrial", icon: "🏭" },
    { id: "advanced", label: "Advanced", icon: "🚀" },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Futuristic background overlay */}
        <div
          className="fixed inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/90 to-indigo-900/90 backdrop-blur-sm"
          onClick={onClose}
        ></div>

        {/* Modern modal panel */}
        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-6xl mx-auto overflow-hidden border border-white/20">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🏭</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {initialData ? "Edit" : "Create"} Product
                  </h2>
                  <p className="text-blue-100 text-sm">
                    Advanced product management with industry features
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-1 p-4 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-white text-blue-600 shadow-sm border border-blue-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 max-h-[70vh] overflow-y-auto"
          >
            {/* Basic Information Tab */}
            {activeTab === "basic" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="e.g., High-Grade Steel Pipe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Item Code *
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.itemCode}
                      onChange={(e) => handleChange("itemCode", e.target.value)}
                      placeholder="e.g., SP-2IN-001"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Brand
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.brand}
                      onChange={(e) => handleChange("brand", e.target.value)}
                      placeholder="e.g., IndustrialCorp"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Product Category
                  </label>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1">
                      <CategorySelector
                        categories={categories}
                        selectedCategoryId={form.primaryCategoryId}
                        onCategoryChange={handleCategoryChange}
                        loading={loadingCategories}
                        label=""
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddCategoryModal(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap h-[42px]"
                      title="Add New Category"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Category
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cost Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.costPrice}
                      onChange={(e) =>
                        handleChange("costPrice", e.target.value)
                      }
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Selling Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.sellingPrice}
                      onChange={(e) =>
                        handleChange("sellingPrice", e.target.value)
                      }
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      GST %
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.gstPercent}
                      onChange={(e) =>
                        handleChange("gstPercent", e.target.value)
                      }
                      placeholder="18.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    placeholder="Brief product description"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Detailed Description
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.detailedDescription}
                    onChange={(e) =>
                      handleChange("detailedDescription", e.target.value)
                    }
                    placeholder="Comprehensive product details, specifications, applications..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Media Tab */}
            {activeTab === "media" && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <h3 className="text-sm font-semibold text-blue-900">
                        Product Images
                      </h3>
                      <p className="text-sm text-blue-700 mt-1">
                        Upload multiple images. The first image will be the
                        primary image displayed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    id="imageUpload"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="imageUpload"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-700">
                        Click to upload images
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        PNG, JPG, GIF up to 10MB each
                      </p>
                    </div>
                    {uploadingImages && (
                      <div className="text-blue-600 text-sm font-medium">
                        Uploading...
                      </div>
                    )}
                  </label>
                </div>

                {/* Image Gallery */}
                {form.imageUrls && form.imageUrls.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Uploaded Images ({form.imageUrls.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {form.imageUrls.map((url, index) => (
                        <div
                          key={index}
                          className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-square"
                        >
                          <img
                            src={url}
                            alt={`Product ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {index === 0 && (
                            <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                              PRIMARY
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {index !== 0 && (
                              <button
                                type="button"
                                onClick={() => handleSetPrimaryImage(index)}
                                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                                title="Set as primary"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors"
                              title="Remove image"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SKUs & Barcodes Tab */}
            {activeTab === "skus" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Product SKUs & Barcodes
                  </h3>
                  <button
                    type="button"
                    onClick={addSku}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    + Add SKU
                  </button>
                </div>

                {form.skus.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>
                      No SKUs added yet. Click "Add SKU" to create the first SKU
                      for this product.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.skus.map((sku, index) => (
                      <div
                        key={sku.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold text-gray-900">
                            SKU #{index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeSku(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              SKU Code *
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.sku}
                              onChange={(e) =>
                                updateSku(index, "sku", e.target.value)
                              }
                              placeholder="e.g., SKU-001"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Barcode *
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.barcode}
                              onChange={(e) =>
                                updateSku(index, "barcode", e.target.value)
                              }
                              placeholder="e.g., 8901234567890"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              SKU Name
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.name}
                              onChange={(e) =>
                                updateSku(index, "name", e.target.value)
                              }
                              placeholder="e.g., Black Size 42"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Cost Price
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.costPrice}
                              onChange={(e) =>
                                updateSku(index, "costPrice", e.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Selling Price
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.sellingPrice}
                              onChange={(e) =>
                                updateSku(index, "sellingPrice", e.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Weight (kg)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.weight}
                              onChange={(e) =>
                                updateSku(index, "weight", e.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Dimensions
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={sku.dimensions}
                            onChange={(e) =>
                              updateSku(index, "dimensions", e.target.value)
                            }
                            placeholder="e.g., 100cm x 50cm x 25cm"
                          />
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={sku.description}
                            onChange={(e) =>
                              updateSku(index, "description", e.target.value)
                            }
                            placeholder="SKU-specific description"
                            rows={2}
                          />
                        </div>

                        <div className="mt-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={sku.isActive}
                              onChange={(e) =>
                                updateSku(index, "isActive", e.target.checked)
                              }
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Active
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Specifications Tab */}
            {activeTab === "specifications" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Product Type
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.itemType}
                      onChange={(e) => handleChange("itemType", e.target.value)}
                    >
                      <option value="">Select Type</option>
                      {productTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Unit of Measure
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.unitOfMeasure}
                      onChange={(e) =>
                        handleChange("unitOfMeasure", e.target.value)
                      }
                    >
                      <option value="">Select Unit</option>
                      {unitsOfMeasure.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Material
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.material}
                      onChange={(e) => handleChange("material", e.target.value)}
                    >
                      <option value="">Select Material</option>
                      {materials.map((material) => (
                        <option key={material} value={material}>
                          {material}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.weight}
                      onChange={(e) => handleChange("weight", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Dimensions
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.dimensions}
                      onChange={(e) =>
                        handleChange("dimensions", e.target.value)
                      }
                      placeholder="e.g., 100cm x 50cm x 25cm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Grade
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.grade}
                      onChange={(e) => handleChange("grade", e.target.value)}
                    >
                      <option value="">Select Grade</option>
                      {grades.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Brand
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.brand}
                      onChange={(e) => handleChange("brand", e.target.value)}
                      placeholder="e.g., Tata Steel"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Model
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.model}
                      onChange={(e) => handleChange("model", e.target.value)}
                      placeholder="e.g., TS-2024"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Variants Tab */}
            {activeTab === "variants" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pack Size Variants & Pricing
                  </h3>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    + Add Pack Size
                  </button>
                </div>

                {form.variants.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-4 block">📦</span>
                    <p>
                      No pack size variants added yet. Click "Add Pack Size" to
                      create pricing variants.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.variants.map((variant, index) => (
                      <div
                        key={variant.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-gray-900">
                            Pack Size {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Pack Size
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., 1.5 LT, 7.5 KG, 4 LT"
                              value={variant.name}
                              onChange={(e) =>
                                updateVariant(index, "name", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price (₹)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={variant.price}
                              onChange={(e) =>
                                updateVariant(index, "price", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bulk P/Ltr (₹)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={variant.bulkPricePerLiter}
                              onChange={(e) =>
                                updateVariant(
                                  index,
                                  "bulkPricePerLiter",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              SKU
                            </label>
                            <input
                              type="text"
                              placeholder="Product SKU for this pack size"
                              value={variant.sku}
                              onChange={(e) =>
                                updateVariant(index, "sku", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Weight (kg)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={variant.weight}
                              onChange={(e) =>
                                updateVariant(index, "weight", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Packaging Tab */}
            {activeTab === "packaging" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Packaging Levels
                  </h3>
                  <button
                    type="button"
                    onClick={addPackagingLevel}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Packaging Level
                  </button>
                </div>

                {form.packagingLevels.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-4 block">📦</span>
                    <p>
                      No packaging levels defined. Click "Add Packaging Level"
                      to define packaging hierarchy.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.packagingLevels.map((level, index) => (
                      <div
                        key={level.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-gray-900">
                            Level {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removePackagingLevel(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input
                            type="text"
                            placeholder="Level (e.g., Unit, Box, Carton, Pallet)"
                            value={level.level}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "level",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="text"
                            placeholder="Description"
                            value={level.description}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="number"
                            placeholder="Quantity per level"
                            value={level.quantity}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "quantity",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Weight (kg)"
                            value={level.weight}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "weight",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Industrial Features Tab */}
            {activeTab === "industrial" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      HSN Code
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.hsCode}
                      onChange={(e) => handleChange("hsCode", e.target.value)}
                      placeholder="e.g., 7304.19.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Country of Origin
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.countryOfOrigin}
                      onChange={(e) =>
                        handleChange("countryOfOrigin", e.target.value)
                      }
                      placeholder="e.g., India"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isHazmat"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={form.isHazmat}
                      onChange={(e) =>
                        handleChange("isHazmat", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="isHazmat"
                      className="ml-2 text-sm font-medium text-gray-700"
                    >
                      Hazardous Material
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="batchControlled"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={form.batchControlled}
                      onChange={(e) =>
                        handleChange("batchControlled", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="batchControlled"
                      className="ml-2 text-sm font-medium text-gray-700"
                    >
                      Batch Controlled
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="discountEligibility"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={form.discountEligibility}
                      onChange={(e) =>
                        handleChange("discountEligibility", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="discountEligibility"
                      className="ml-2 text-sm font-medium text-gray-700"
                    >
                      Discount Eligible
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Shelf Life (months)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.shelfLifeMonths}
                      onChange={(e) =>
                        handleChange("shelfLifeMonths", e.target.value)
                      }
                      placeholder="12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Lead Time (days)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.leadTime}
                      onChange={(e) => handleChange("leadTime", e.target.value)}
                      placeholder="7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Storage Conditions
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.storageConditions}
                    onChange={(e) =>
                      handleChange("storageConditions", e.target.value)
                    }
                    placeholder="e.g., Store in cool, dry place. Temperature: 15-25°C"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Special Instructions
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.specialInstructions}
                    onChange={(e) =>
                      handleChange("specialInstructions", e.target.value)
                    }
                    placeholder="Special handling, safety instructions, etc."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Advanced Tab */}
            {activeTab === "advanced" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Min Order Quantity
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.minimumOrderQuantity}
                      onChange={(e) =>
                        handleChange("minimumOrderQuantity", e.target.value)
                      }
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Max Order Quantity
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.maximumOrderQuantity}
                      onChange={(e) =>
                        handleChange("maximumOrderQuantity", e.target.value)
                      }
                      placeholder="1000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reorder Point
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.reorderPoint}
                      onChange={(e) =>
                        handleChange("reorderPoint", e.target.value)
                      }
                      placeholder="10"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={form.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                  />
                  <label
                    htmlFor="isActive"
                    className="ml-2 text-sm font-medium text-gray-700"
                  >
                    Product is Active
                  </label>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-red-700 font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {submitting
                  ? "Saving..."
                  : initialData
                  ? "Update Product"
                  : "Create Product"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Category
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddCategoryModal(false);
                  setNewCategory({ name: "", code: "", parentId: null });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter category name"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Code (Optional)
                </label>
                <input
                  type="text"
                  value={newCategory.code}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      code: e.target.value.toUpperCase(),
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="Auto-generated if empty"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Category (Optional)
                </label>
                <select
                  value={newCategory.parentId || ""}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      parentId: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">None (Root Category)</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.path || cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddCategoryModal(false);
                  setNewCategory({ name: "", code: "", parentId: null });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCategory}
                disabled={addingCategory || !newCategory.name.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingCategory ? "Adding..." : "Add Category"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductForm;
